import React, { lazy, Suspense, useContext, useMemo } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import { NexusProvider } from '@return-rabbit/nexus';
import '@return-rabbit/nexus/build/index.css';
import { Helmet } from 'react-helmet';
import _get from 'lodash/get';

import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import * as RouteGuard from 'components/RouteGuards';
import { AuthContext } from 'context/AuthContext';
import useLogRocket from 'utils/hooks/useLogRocket';
import useNexusConfig from 'utils/hooks/useNexusConfig';
import useSegment from 'utils/hooks/useSegment';
import logError from 'utils/extra/logError';
import { ROUTES } from 'dictionary/routes';

const Login = lazy(() => import('views/Login'));
const SignIn = lazy(() => import('views/SignIn'));
const Logout = lazy(() => import('views/Logout'));
const GiftReturnLogin = lazy(() => import('views/GiftReturnLogin'));
const QrShippingLabel = lazy(() => import('views/QrShippingLabel'));
const DropoffLocations = lazy(() => import('views/DropoffLocations'));
const ShipmentTracking = lazy(() => import('views/ShipmentTracking'));
const Account = lazy(() => import('views/Account'));
const OrderDetails = lazy(() => import('views/Orders/OrderDetails'));
const OrderService = lazy(() => import('views/OrderService'));
const Error = lazy(() => import('views/Errors/ErrorPage'));
const NotFound = lazy(() => import('views/Errors/NotFound'));
const RequestDetails = lazy(() => import('views/Requests/RequestDetails'));

export default function App() {
  const { authToken, userData, getStoreDetails, masterData, storeProperties } =
    useContext(AuthContext);

  const segment = useSegment();
  const logRocket = useLogRocket();
  const { axiosConfig } = useNexusConfig();

  const storeInfo = getStoreDetails();
  const storeName = _get(storeInfo, 'name', '');
  const favicon = _get(storeInfo, 'favicon', '');

  const userInfo = useMemo(() => {
    if (!userData) return null;

    return {
      id: _get(userData, 'userId'),
      email: _get(userData, 'email'),
      firstName: _get(userData, 'firstName'),
      lastName: _get(userData, 'lastName'),
    };
  }, [userData]);

  const analytics = useMemo(() => {
    return {
      segment,
      logRocket,
    };
  }, [segment, logRocket]);

  return (
    <>
      <Helmet>
        <title>{storeName ? `${storeName} Support` : 'Returns Portal'}</title>
        {favicon && <link rel="icon" href={favicon} />}
      </Helmet>
      <ErrorBoundary>
        <NexusProvider
          key={authToken}
          mode="SHOPPER"
          axiosConfig={axiosConfig}
          masterData={masterData}
          storeProperties={storeProperties}
          logError={logError}
          userInfo={userInfo}
          analytics={analytics}
        >
          <Suspense fallback={<Loader />}>
            <Switch>
              <RouteGuard.Auth path={ROUTES.LOGIN}>
                <Login />
              </RouteGuard.Auth>
              <RouteGuard.Auth path={ROUTES.GIFT_LOGIN}>
                <GiftReturnLogin />
              </RouteGuard.Auth>

              <RouteGuard.Public path={ROUTES.SIGN_IN}>
                <SignIn />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.LOGOUT}>
                <Logout />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.ERROR}>
                <Error />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.NOT_FOUND}>
                <NotFound />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.TRACKING.DETAILS}>
                <ShipmentTracking />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.QR_SHIPPING_LABEL}>
                <QrShippingLabel />
              </RouteGuard.Public>
              <RouteGuard.Public path={ROUTES.DROPOFF_LOCATIONS}>
                <DropoffLocations />
              </RouteGuard.Public>

              <RouteGuard.Protected path={ROUTES.ACCOUNT.RETURNS_DETAILS}>
                <RequestDetails />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ORDER_DETAILS}>
                <OrderDetails />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.REQUEST.ROOT}>
                <OrderService />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ACCOUNT.ROOT}>
                <Account />
              </RouteGuard.Protected>
              <RouteGuard.Protected path={ROUTES.ROOT}>
                <Redirect to={ROUTES.ACCOUNT.ROOT} />
              </RouteGuard.Protected>
            </Switch>
          </Suspense>
        </NexusProvider>
      </ErrorBoundary>
    </>
  );
}
